<template>
  <div class="emp">
    <StateDisplay v-if="loading" :loading="true" class="mt-8" />

    <template v-else>
      <!-- ═══════════ Kopfzeile: Sections + Modus + Zeitraum ═══════════ -->
      <div class="emp-toolbar">
        <div class="emp-sections">
          <div
            v-for="sec in sections" :key="sec.value"
            class="emp-sections__opt"
            :class="{ active: section === sec.value }"
            @click="section = sec.value"
          >
            <v-icon x-small class="mr-1" :color="section === sec.value ? '#7b5cd6' : undefined">{{ sec.icon }}</v-icon>
            {{ sec.text }}
          </div>
        </div>
        <v-spacer />
        <div
          class="emp-mode emp-mode--clickable"
          :class="settingsDoc.testMode ? 'emp-mode--test' : 'emp-mode--live'"
          :title="settingsDoc.testMode ? 'Klicken zum LIVE-Schalten' : 'Klicken für Testmodus'"
          @click="toggleMode"
        >
          <span class="emp-mode__dot" />
          {{ settingsDoc.testMode ? "Testmodus" : "LIVE" }}
          <v-icon x-small class="ml-1">mdi-swap-horizontal</v-icon>
        </div>
        <v-select
          v-if="section === 'automations'"
          v-model="rangeDays"
          :items="rangeItems"
          dense
          solo
          flat
          hide-details
          class="emp-range"
        />
      </div>

      <template v-if="section === 'automations'">
      <!-- ═══════════ KPI-Kacheln ═══════════ -->
      <div class="emp-kpis">
        <div class="emp-kpi">
          <div class="emp-kpi__value">{{ totals.captured }}</div>
          <div class="emp-kpi__label">Abbrecher erfasst</div>
        </div>
        <div class="emp-kpi">
          <div class="emp-kpi__value">
            {{ totals.withEmail }}
            <span class="emp-kpi__pct">{{ pct(totals.withEmail, totals.captured) }}</span>
          </div>
          <div class="emp-kpi__label">mit E-Mail</div>
        </div>
        <div class="emp-kpi">
          <div class="emp-kpi__value">
            {{ totals.mail1 }}
            <span v-if="totals.dry1" class="emp-kpi__dry">+{{ totals.dry1 }} dry</span>
          </div>
          <div class="emp-kpi__label">Mail 1 versendet</div>
        </div>
        <div class="emp-kpi">
          <div class="emp-kpi__value">
            {{ totals.mail2 }}
            <span v-if="totals.dry2" class="emp-kpi__dry">+{{ totals.dry2 }} dry</span>
          </div>
          <div class="emp-kpi__label">Mail 2 versendet</div>
        </div>
        <div class="emp-kpi">
          <div class="emp-kpi__value">
            {{ totals.delivered }}
            <span class="emp-kpi__pct">{{ pct(totals.delivered, totals.mail1 + totals.mail2) }}</span>
          </div>
          <div class="emp-kpi__label">Zugestellt <span class="emp-kpi__sub">Resend</span></div>
        </div>
        <div class="emp-kpi">
          <div class="emp-kpi__value">{{ totals.clicked }}</div>
          <div class="emp-kpi__label">Klicks</div>
        </div>
        <div class="emp-kpi" :class="{ 'emp-kpi--bad': totals.complained }">
          <div class="emp-kpi__value">{{ totals.complained }}</div>
          <div class="emp-kpi__label">Spam / Bounce <span class="emp-kpi__sub">Resend</span></div>
        </div>
        <div class="emp-kpi emp-kpi--accent">
          <div class="emp-kpi__value">
            {{ totals.converted }}
            <span v-if="totals.codeRedeemed" class="emp-kpi__pct">{{ totals.codeRedeemed }}× Code</span>
          </div>
          <div class="emp-kpi__label">Conversions</div>
        </div>
      </div>

      <!-- ═══════════ Shop-Tabelle ═══════════ -->
      <div class="emp-card">
        <div class="emp-card__head">
          <v-icon small class="emp-card__icon">mdi-storefront-outline</v-icon>
          Nach Shop
        </div>
        <table class="emp-table">
          <thead>
            <tr>
              <th>Shop</th>
              <th>Erfasst</th>
              <th>mit E-Mail</th>
              <th>Mail 1</th>
              <th>Mail 2</th>
              <th>Zugestellt</th>
              <th>Klicks</th>
              <th>Spam / Bounce</th>
              <th>Code eingelöst</th>
              <th>Conversions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in statRows" :key="row.shop">
              <td class="emp-table__shop">{{ shopLabel(row.shop) }}</td>
              <td>{{ row.captured }}</td>
              <td>{{ row.withEmail }} <span class="emp-table__pct">{{ pct(row.withEmail, row.captured) }}</span></td>
              <td>{{ row.mail1 }}<span v-if="row.dry1" class="emp-table__dry"> +{{ row.dry1 }}</span></td>
              <td>{{ row.mail2 }}<span v-if="row.dry2" class="emp-table__dry"> +{{ row.dry2 }}</span></td>
              <td>{{ row.delivered }} <span class="emp-table__pct">{{ pct(row.delivered, row.mail1 + row.mail2) }}</span></td>
              <td>{{ row.clicked }}</td>
              <td :class="{ 'emp-table__bad': row.complained }">{{ row.complained }}</td>
              <td>{{ row.codeRedeemed }}</td>
              <td class="emp-table__strong">{{ row.converted }}</td>
            </tr>
          </tbody>
        </table>
        <div class="emp-hint mt-1">
          Zugestellt/Spam nur für Resend-Mails messbar (Mail 1 über Workspace liefert keine Zustell-Events) · Klicks kanalunabhängig über den Mail-Link.
        </div>
      </div>

      <!-- ═══════════ Automationen ═══════════ -->
      <div class="emp-card">
        <div class="emp-card__head">
          <v-icon small class="emp-card__icon">mdi-robot-outline</v-icon>
          Automationen
          <v-spacer />
          <v-btn color="#7b5cd6" dark depressed small @click="openNewAutomation">
            <v-icon small left>mdi-plus</v-icon> Neue Automation
          </v-btn>
        </div>
        <div class="emp-autos">
          <div v-for="a in automations" :key="a.id" class="emp-auto" :class="{ 'emp-auto--off': a.active === false }">
            <div class="emp-auto__flow">
              <span class="emp-auto__event">{{ eventLabel(a.event) }}</span>
              <v-icon small class="mx-1">mdi-arrow-right</v-icon>
              <span class="emp-auto__delay">{{ a.delayHours }}h</span>
              <v-icon small class="mx-1">mdi-arrow-right</v-icon>
              <span class="emp-auto__tpl">{{ templateName(a.templateId) }}</span>
            </div>
            <div class="emp-auto__name">{{ a.name }}</div>
            <div class="emp-auto__chips">
              <span v-if="a.requiresOptIn" class="emp-tplcard__badge">nur Opt-ins</span>
              <span v-if="a.discount && a.discount.type === 'unique'" class="emp-tplcard__badge">Einmal-Codes</span>
              <span v-if="a.discount && a.discount.type === 'shared'" class="emp-tplcard__badge">Code: {{ a.discount.code }}</span>
            </div>
            <div class="emp-auto__actions">
              <v-switch
                :input-value="a.active !== false"
                inset dense hide-details
                class="mt-0 pt-0"
                @change="toggleAutomation(a, $event)"
              />
              <v-btn
                v-if="!a.id.startsWith('checkout-abandoned-')"
                icon small title="Löschen"
                @click="deleteAutomation(a)"
              ><v-icon small>mdi-delete-outline</v-icon></v-btn>
              <v-btn icon small @click="openEditAutomation(a)"><v-icon small>mdi-pencil</v-icon></v-btn>
            </div>
          </div>
        </div>
      </div>
      </template>

      <!-- ═══════════ Einstellungen (eigene Section) ═══════════ -->
      <div v-if="section === 'settings'" class="emp-card">
        <div class="emp-card__head">
          <v-icon small class="emp-card__icon">mdi-tune-variant</v-icon>
          Einstellungen
          <v-spacer />
          <transition name="fade">
            <span v-if="savedAt" class="emp-saved">✓ gespeichert</span>
          </transition>
          <v-btn color="#7b5cd6" dark depressed small :loading="saving" @click="saveSettings">
            Speichern
          </v-btn>
        </div>

        <div class="emp-settings">
          <div class="emp-settings__block">
            <div class="emp-label">Modus</div>
            <div
              class="emp-mode-switch"
              :class="{ 'emp-mode-switch--live': !form.testMode }"
              @click="form.testMode = !form.testMode"
            >
              <div class="emp-mode-switch__opt" :class="{ active: form.testMode }">
                <v-icon x-small class="mr-1">mdi-flask-outline</v-icon> Test
              </div>
              <div class="emp-mode-switch__opt" :class="{ active: !form.testMode }">
                <v-icon x-small class="mr-1">mdi-rocket-launch-outline</v-icon> Live
              </div>
            </div>
            <div v-if="form.testMode" class="emp-hint mt-2">
              Mails gehen nur an die Test-Adressen; echte Kunden werden als Dry-Run gezählt.
            </div>
            <div v-else class="emp-hint emp-hint--warn mt-2">
              ⚠️ Echte Kunden erhalten Mails, sobald gespeichert wird.
            </div>
          </div>

          <div class="emp-settings__block">
            <div class="emp-label">Aktive Shops</div>
            <v-select
              v-model="form.shops"
              :items="knownShops"
              multiple chips small-chips dense outlined hide-details
            />
          </div>

          <div class="emp-settings__block emp-settings__block--wide">
            <div class="emp-label">Test-Adressen</div>
            <v-combobox
              v-model="form.allowedTestEmails"
              multiple chips small-chips deletable-chips dense outlined hide-details
              placeholder="E-Mail eingeben und Enter drücken"
            />
          </div>
        </div>

        <div class="emp-label mt-5 mb-2">Versandkanal je Shop</div>
        <div class="emp-senders">
          <div v-for="shop in senderShops" :key="shop" class="emp-sender">
            <div class="emp-sender__shop">{{ shopLabel(shop) }}</div>
            <div class="emp-sender__row">
              <span class="emp-sender__mail">Mail 1</span>
              <div class="emp-channel">
                <div
                  v-for="c in channelItems" :key="c.value"
                  class="emp-channel__opt"
                  :class="{ active: senderFor(shop).mail1 === c.value }"
                  @click="$set(senderFor(shop), 'mail1', c.value)"
                >{{ c.text }}</div>
              </div>
            </div>
            <div class="emp-sender__row">
              <span class="emp-sender__mail">Mail 2</span>
              <div class="emp-channel">
                <div
                  v-for="c in channelItems" :key="c.value"
                  class="emp-channel__opt"
                  :class="{ active: senderFor(shop).mail2 === c.value }"
                  @click="$set(senderFor(shop), 'mail2', c.value)"
                >{{ c.text }}</div>
              </div>
            </div>
            <div class="emp-sender__from mt-2">
              <v-text-field
                v-model="senderFor(shop).resendFromName"
                label="Absender-Name"
                placeholder="Artquarell"
                dense outlined hide-details
                :disabled="senderFor(shop).mail1 !== 'resend' && senderFor(shop).mail2 !== 'resend'"
              />
              <v-text-field
                v-model="senderFor(shop).resendFrom"
                label="Resend-Absender (E-Mail)"
                placeholder="angebot@news.domain.com"
                dense outlined hide-details
                :disabled="senderFor(shop).mail1 !== 'resend' && senderFor(shop).mail2 !== 'resend'"
              />
            </div>
            <div
              v-if="senderFor(shop).resendFrom && (senderFor(shop).mail1 === 'resend' || senderFor(shop).mail2 === 'resend')"
              class="emp-sender__preview"
            >
              Erscheint als: <strong>{{ senderFor(shop).resendFromName || senderFor(shop).resendFrom.split('@')[0] }}</strong>
              <span class="emp-sender__preview-addr">&lt;{{ senderFor(shop).resendFrom }}&gt;</span>
            </div>
          </div>
        </div>
        <div class="emp-hint mt-2">
          <strong>Workspace</strong> = normales Postfach, landet eher im Posteingang ·
          <strong>Resend</strong> = Marketing-Subdomain mit eigener Reputation, landet eher unter „Werbung“.
          Antworten gehen immer an die Support-Adresse des Shops.
        </div>
      </div>

      <!-- ═══════════ Template-Builder ═══════════ -->
      <div v-if="section === 'templates'" class="emp-card">
        <div class="emp-card__head">
          <v-btn v-if="builderView === 'edit'" icon small class="mr-1" @click="builderView = 'grid'">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-icon small class="emp-card__icon">mdi-code-tags</v-icon>
          {{ builderView === 'grid' ? 'Templates' : 'Template bearbeiten' }}
          <v-spacer />
          <v-btn v-if="builderView === 'grid'" color="#7b5cd6" dark depressed small @click="createBlankTemplate">
            <v-icon small left>mdi-plus</v-icon> Neues Template
          </v-btn>
          <template v-if="builderView === 'edit'">
            <transition name="fade">
              <span v-if="savedTplAt" class="emp-saved">✓ gespeichert</span>
            </transition>
            <span v-if="tplError" class="emp-hint emp-hint--warn mr-3">{{ tplError }}</span>
            <v-btn color="#7b5cd6" dark depressed small :loading="savingTpl" @click="saveTemplate">
              Template speichern
            </v-btn>
          </template>
        </div>

        <!-- Galerie: Karten mit Live-Vorschau -->
        <div v-if="builderView === 'grid'" class="emp-gallery">
          <div v-for="tpl in templates" :key="tpl.id" class="emp-tplcard">
            <div class="emp-tplcard__preview" @click="openEditor(tpl.id)">
              <div class="emp-tplcard__scale" v-html="cardPreview(tpl)" />
              <div class="emp-tplcard__hover">
                <v-icon color="white">mdi-pencil</v-icon>
              </div>
            </div>
            <div class="emp-tplcard__meta">
              <div class="emp-tplcard__name">
                {{ tpl.name || tpl.id }}
                <div class="emp-tplcard__badges">
                  <span v-if="templateIsLive(tpl.id)" class="emp-live-badge">LIVE</span>
                  <span v-for="u in templateUsage(tpl.id)" :key="u" class="emp-tplcard__badge">{{ u }}</span>
                </div>
              </div>
              <v-spacer />
              <v-btn icon small title="Duplizieren" @click="duplicateTemplate(tpl)">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
              <v-btn icon small title="Löschen" @click="deleteTemplate(tpl)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
              <v-btn icon small title="Bearbeiten" @click="openEditor(tpl.id)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Editor -->
        <template v-else>
        <v-text-field
          v-model="tplName"
          label="Template-Name"
          dense outlined hide-details
          class="emp-tpl-name mb-3"
        />

        <div class="emp-builder">
          <!-- Editor links -->
          <div class="emp-editor">
            <div class="emp-editor__bar">
              <div class="emp-toggle">
                <div class="emp-toggle__opt" :class="{ active: editorTab === 'html' }" @click="editorTab = 'html'">HTML</div>
                <div class="emp-toggle__opt" :class="{ active: editorTab === 'texts' }" @click="switchToTexts">Platzhalter</div>
                <div class="emp-toggle__opt" :class="{ active: editorTab === 'allLangs' }" @click="switchToAllLangs">Alle Sprachen</div>
              </div>
              <v-spacer />
              <v-select
                v-if="editorTab === 'texts'"
                v-model="lang"
                :items="langItems"
                dense solo flat hide-details
                class="emp-lang"
              />
              <v-btn icon small class="ml-1" title="In Zwischenablage kopieren" @click="copyEditor">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
            <textarea
              v-if="editorTab === 'html'"
              v-model="tplHtml"
              class="emp-code"
              spellcheck="false"
            />
            <textarea
              v-else-if="editorTab === 'texts'"
              v-model="textsJson"
              class="emp-code"
              spellcheck="false"
            />
            <textarea
              v-else
              v-model="allLangsJson"
              class="emp-code"
              spellcheck="false"
            />
            <div class="emp-placeholders">
              <div class="emp-label mb-1">Platzhalter</div>
              <div class="emp-placeholders__list">
                <code v-for="ph in placeholderDocs" :key="ph.token" :title="ph.hint">{{ ph.token }}</code>
              </div>
              <div class="emp-hint mt-1">
                <code>{{ token("t.xyz") }}</code> oder <code>{{ token("xyz") }}</code> = Wert aus dem Platzhalter-JSON
                (Text <strong>oder Bild-URL</strong>) · <code>{{ ifSyntax }}</code> Block nur wenn Wert gesetzt ·
                <code>{{ token("optoutLink") }}</code> ist Pflicht.
                Für KI-Änderungen: HTML kopieren → KI → zurückkleben (Platzhalter stehen lassen).
              </div>
            </div>
          </div>

          <!-- Vorschau rechts -->
          <div class="emp-preview">
            <div class="emp-preview__bar">
              <div class="emp-label mb-0">Vorschau</div>
              <v-spacer />
              <v-select
                v-model="lang"
                :items="langItems"
                dense solo flat hide-details
                class="emp-lang"
              />
            </div>
            <div class="emp-preview__subject">Betreff: <strong>{{ previewSubject }}</strong></div>
            <div class="emp-preview__frame" v-html="previewHtml" />
          </div>
        </div>
        </template>
      </div>
      <!-- ═══════════ Kampagnen ═══════════ -->
      <div v-if="section === 'campaigns'" class="emp-card">
        <div class="emp-card__head">
          <v-icon small class="emp-card__icon">mdi-send-outline</v-icon>
          Kampagnen
          <v-spacer />
          <v-btn color="#7b5cd6" dark depressed small @click="openNewCampaign">
            <v-icon small left>mdi-plus</v-icon> Neue Kampagne
          </v-btn>
        </div>

        <div v-if="!campaigns.length" class="emp-hint">
          Noch keine Kampagnen. Lege eine an: Template wählen, Zielgruppe zählen, Test senden, los.
        </div>

        <div class="emp-camps">
          <div v-for="c in campaigns" :key="c.id" class="emp-camp">
            <!-- Kopf -->
            <div class="emp-camp__head">
              <div class="emp-camp__title">
                <span class="emp-camp__name">{{ c.name }}</span>
                <span class="emp-camp__status" :class="'emp-camp__status--' + (c.status || 'draft')">{{ statusLabelFor(c) }}</span>
              </div>
              <div class="emp-camp__meta">
                {{ shopLabel(c.shopCode) }} · {{ templateName(c.templateId) }} · {{ (c.lang || 'de').toUpperCase() }}
                · {{ c.channel === 'workspace' ? 'Workspace' : 'Resend' }}
                <span v-if="c.segment === 'buyers'"> · Käufer {{ fmtDate(c.segmentFrom) }}–{{ fmtDate(c.segmentTo) }}</span>
                <span v-if="c.discount && c.discount.type === 'shared'"> · Code: {{ c.discount.code }}</span>
              </div>
              <v-spacer />
              <!-- Aktionen rechts: Test · Bearbeiten/Löschen · Play/Pause -->
              <v-btn small text @click="openTestDialog(c)">
                <v-icon small left>mdi-email-check-outline</v-icon> Testen
              </v-btn>
              <v-btn small icon @click="openEditCampaign(c)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-btn
                v-if="!['building', 'sending'].includes(c.status)"
                small icon title="Löschen" @click="deleteCampaign(c)"
              ><v-icon small>mdi-delete-outline</v-icon></v-btn>
              <v-btn
                v-if="['building', 'sending'].includes(c.status)"
                icon color="warning" title="Pausieren"
                @click="campaignAction({ action: 'pause', campaignId: c.id })"
              ><v-icon>mdi-pause-circle</v-icon></v-btn>
              <v-btn
                v-else
                icon color="#7b5cd6"
                :disabled="!c.testSentAt"
                :title="c.testSentAt ? 'Kampagne starten' : 'Erst Test-Mail senden'"
                @click="confirmStart(c)"
              ><v-icon large>mdi-play-circle</v-icon></v-btn>
            </div>

            <!-- KPI-Kacheln (wie bei den Checkout-Erinnerungen) -->
            <div class="emp-camp__kpis">
              <div class="emp-kpi emp-kpi--sm">
                <div class="emp-kpi__value">
                  <template v-if="audienceFor(c).count != null">{{ audienceFor(c).count.toLocaleString('de-DE') }}</template>
                  <v-progress-circular v-else-if="canCount(c)" indeterminate size="14" width="2" />
                  <span v-else class="emp-kpi__na">—</span>
                </div>
                <div class="emp-kpi__label">
                  {{ c.segment === 'buyers' ? 'Käufer' : 'Opt-ins' }}
                  <v-btn v-if="canCount(c)" icon x-small class="ml-1" :loading="countingId === c.id" @click="countAudience(c, true)">
                    <v-icon x-small>mdi-refresh</v-icon>
                  </v-btn>
                  <span v-if="audienceFor(c).at" class="emp-kpi__sub">{{ audienceAge(c) }}</span>
                  <span v-else-if="!canCount(c)" class="emp-kpi__sub">{{ c.segment === 'buyers' ? 'Zeitraum fehlt' : 'nur DE' }}</span>
                </div>
              </div>
              <div class="emp-kpi emp-kpi--sm">
                <div class="emp-kpi__value">{{ (c.counters && c.counters.sent || 0).toLocaleString('de-DE') }}</div>
                <div class="emp-kpi__label">Versendet</div>
              </div>
              <div class="emp-kpi emp-kpi--sm">
                <div class="emp-kpi__value">{{ campaignOpen(c).toLocaleString('de-DE') }}</div>
                <div class="emp-kpi__label">Offen</div>
              </div>
              <div class="emp-kpi emp-kpi--sm">
                <div class="emp-kpi__value">{{ (c.counters && c.counters.skipped || 0).toLocaleString('de-DE') }}</div>
                <div class="emp-kpi__label">Übersprungen</div>
              </div>
              <template v-if="c.channel !== 'workspace'">
                <div class="emp-kpi emp-kpi--sm">
                  <div class="emp-kpi__value">
                    {{ cnt(c, 'delivered').toLocaleString('de-DE') }}
                    <span class="emp-kpi__pct">{{ pct(cnt(c, 'delivered'), c.counters && c.counters.sent) }}</span>
                  </div>
                  <div class="emp-kpi__label">Zugestellt</div>
                </div>
                <div class="emp-kpi emp-kpi--sm" :class="{ 'emp-kpi--bad': cnt(c, 'complained') || cnt(c, 'bounced') }">
                  <div class="emp-kpi__value">{{ (cnt(c, 'complained') + cnt(c, 'bounced')).toLocaleString('de-DE') }}</div>
                  <div class="emp-kpi__label">Spam / Bounce</div>
                </div>
              </template>
              <div v-else class="emp-kpi emp-kpi--sm emp-kpi--muted">
                <div class="emp-kpi__value"><span class="emp-kpi__na">—</span></div>
                <div class="emp-kpi__label">Zustellung nur bei Resend</div>
              </div>
              <div class="emp-kpi emp-kpi--sm">
                <div class="emp-kpi__value">{{ cnt(c, 'clicked').toLocaleString('de-DE') }}</div>
                <div class="emp-kpi__label">Klicks</div>
              </div>
              <div class="emp-kpi emp-kpi--sm emp-kpi--accent">
                <div class="emp-kpi__value">{{ cnt(c, 'conversions').toLocaleString('de-DE') }}</div>
                <div class="emp-kpi__label">Conversions</div>
              </div>
            </div>

            <v-progress-linear
              v-if="c.status === 'sending' || c.status === 'building'"
              :value="c.counters && c.counters.queued ? (100 * (c.counters.sent || 0)) / c.counters.queued : 0"
              :indeterminate="c.status === 'building'"
              height="5" rounded color="#7b5cd6" class="mt-3"
            />
            <div v-if="c.status === 'sending' && c.counters" class="emp-camp__progresslabel">
              {{ (c.counters.sent || 0).toLocaleString('de-DE') }} / {{ (c.counters.queued || 0).toLocaleString('de-DE') }} gesendet
            </div>
            <div v-if="c.error" class="emp-hint emp-hint--warn mt-1">{{ c.error }}</div>
          </div>
        </div>
      </div>

      <!-- ═══ UMFRAGE (eigene Komponente: SurveyResultsPanel) ═══ -->
      <SurveyResultsPanel v-if="section === 'survey'" :campaigns="campaigns" />

      <!-- ═══ Dialog: Test senden ═══ -->
      <v-dialog v-model="testDialog.show" max-width="440">
        <v-card class="emp-dlg">
          <div class="emp-dlg__title">
            <v-icon small class="emp-card__icon">mdi-email-check-outline</v-icon>
            Test-Mail senden
          </div>
          <div class="emp-dlg__body">
            <div class="emp-label">An welche Adresse?</div>
            <div class="emp-channel emp-channel--wrap mb-2">
              <div
                v-for="addr in form.allowedTestEmails" :key="addr"
                class="emp-channel__opt"
                :class="{ active: testDialog.mode === addr }"
                @click="testDialog.mode = addr"
              >{{ addr }}</div>
              <div
                class="emp-channel__opt"
                :class="{ active: testDialog.mode === '__custom__' }"
                @click="testDialog.mode = '__custom__'"
              >Andere…</div>
            </div>
            <v-text-field
              v-if="testDialog.mode === '__custom__'"
              v-model="testDialog.custom"
              label="E-Mail-Adresse"
              dense outlined hide-details
            />
            <div class="emp-hint mt-2">
              Sendet genau eine Mail dieser Kampagne (Betreff mit „[TEST]“). Schaltet danach den Start frei.
            </div>
          </div>
          <div class="emp-dlg__actions">
            <v-btn text @click="testDialog.show = false">Abbrechen</v-btn>
            <v-spacer />
            <v-btn color="#7b5cd6" dark depressed :loading="testingId === (testDialog.campaign && testDialog.campaign.id)" @click="doSendTest">
              Test senden
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- ═══ Dialog: Automation anlegen/bearbeiten ═══ -->
      <v-dialog v-model="autoDialog" max-width="560">
        <v-card class="emp-dlg">
          <div class="emp-dlg__title">
            <v-icon small class="emp-card__icon">mdi-robot-outline</v-icon>
            {{ autoForm.id ? 'Automation bearbeiten' : 'Neue Automation' }}
          </div>
          <div class="emp-dlg__body">
            <div class="emp-label">Auslöser</div>
            <v-select
              v-model="autoForm.event"
              :items="eventItems"
              dense outlined hide-details
              :disabled="!!autoForm.id"
              class="mb-1"
            />
            <div v-if="eventHint" class="emp-hint mb-3">{{ eventHint }}</div>

            <div class="emp-dlg__grid mt-2">
              <div>
                <div class="emp-label">Name</div>
                <v-text-field v-model="autoForm.name" dense outlined hide-details />
              </div>
              <div>
                <div class="emp-label">Wartezeit (Stunden)</div>
                <v-text-field
                  v-model.number="autoForm.delayHours"
                  type="number" dense outlined hide-details
                  :suffix="autoForm.delayHours >= 24 ? '≈ ' + (autoForm.delayHours / 24).toFixed(1) + ' Tage' : ''"
                />
              </div>
            </div>

            <div class="emp-dlg__grid mt-3">
              <div>
                <div class="emp-label">Template</div>
                <div class="emp-tplpick" @click="openTplPicker('auto')">
                  <span class="emp-tplpick__name">{{ templateName(autoForm.templateId) }}</span>
                  <span v-if="templateIsLive(autoForm.templateId)" class="emp-live-badge">LIVE</span>
                  <v-spacer />
                  <v-icon small>mdi-image-search-outline</v-icon>
                </div>
              </div>
              <div v-if="autoForm.event === 'order_placed'">
                <div class="emp-label">Versandkanal</div>
                <div class="emp-channel">
                  <div
                    v-for="c in channelItems" :key="c.value"
                    class="emp-channel__opt"
                    :class="{
                      active: autoForm.channel === c.value,
                      'emp-channel__opt--off': c.value === 'resend' && !resendConfiguredAnywhere,
                    }"
                    @click="c.value === 'resend' && !resendConfiguredAnywhere ? null : $set(autoForm, 'channel', c.value)"
                  >{{ c.text }}</div>
                </div>
              </div>
            </div>

            <div class="emp-label mt-4">Empfänger</div>
            <div class="emp-dlg__switchrow">
              <v-switch
                v-model="autoForm.requiresOptIn"
                inset dense hide-details
                class="mt-0 pt-0"
              />
              <div>
                <div class="emp-dlg__switchlabel">Nur an Marketing-Opt-ins</div>
                <div v-if="!autoForm.requiresOptIn" class="emp-hint emp-hint--warn">
                  ⚠️ Ohne Opt-in-Filter rechtlich heikel (UWG) — nur für transaktionsnahe Erinnerungen vertretbar.
                </div>
              </div>
            </div>

            <div class="emp-label mt-4">Rabatt</div>
            <div class="emp-channel mb-3">
              <div
                v-for="opt in discountTypeItems" :key="opt.value"
                class="emp-channel__opt"
                :class="{ active: autoForm.discountType === opt.value }"
                @click="$set(autoForm, 'discountType', opt.value)"
              >{{ opt.text }}</div>
            </div>
            <template v-if="autoForm.discountType !== 'none'">
              <div class="emp-dlg__grid">
                <div>
                  <div class="emp-label">Art &amp; Höhe</div>
                  <div class="d-flex" style="gap:8px">
                    <div class="emp-channel">
                      <div class="emp-channel__opt" :class="{ active: autoForm.discountValueType === 'fixed' }" @click="$set(autoForm, 'discountValueType', 'fixed')">Betrag</div>
                      <div class="emp-channel__opt" :class="{ active: autoForm.discountValueType === 'percentage' }" @click="$set(autoForm, 'discountValueType', 'percentage')">%</div>
                    </div>
                    <v-text-field
                      v-model.number="autoForm.discountAmount"
                      type="number" dense outlined hide-details
                      style="max-width:110px"
                      :suffix="autoForm.discountValueType === 'percentage' ? '%' : shopCurrency(autoForm)"
                    />
                  </div>
                </div>
                <div v-if="autoForm.discountType === 'unique'">
                  <div class="emp-label">Mindestbestellwert</div>
                  <v-text-field
                    v-model.number="autoForm.discountMinSubtotal"
                    type="number" dense outlined hide-details
                    :suffix="shopCurrency(autoForm)"
                    style="max-width:140px"
                  />
                </div>
                <div v-if="autoForm.discountType === 'shared'">
                  <div class="emp-label">Code (muss in Shopify existieren)</div>
                  <v-text-field v-model="autoForm.discountCode" dense outlined hide-details />
                </div>
              </div>
              <div class="emp-hint mt-2">
                Festbeträge laufen automatisch in der <strong>Shop-Währung</strong> (DE/FR/ES = €, OhMy = $).
                Einmal-Codes erzeugt das System pro Empfänger; die Höhe füllt <code>{{ token("amount") }}</code> im Template.
              </div>
            </template>

            <div class="emp-hint mt-3">Test-Modus &amp; Abmelde-Sperren gelten immer automatisch.</div>
          </div>
          <div class="emp-dlg__actions">
            <v-btn text @click="autoDialog = false">Abbrechen</v-btn>
            <v-spacer />
            <v-btn color="#7b5cd6" dark depressed :loading="savingAuto" @click="saveAutomation">Speichern</v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- ═══ Dialog: Kampagne anlegen/bearbeiten ═══ -->
      <v-dialog v-model="campDialog" max-width="560">
        <v-card class="emp-dlg">
          <div class="emp-dlg__title">
            <v-icon small class="emp-card__icon">mdi-send-outline</v-icon>
            {{ campForm.id ? 'Kampagne bearbeiten' : 'Neue Kampagne' }}
          </div>
          <div class="emp-dlg__body">
            <div class="emp-label">Name</div>
            <v-text-field v-model="campForm.name" placeholder="z. B. Weihnachten 2026 DE" dense outlined hide-details class="mb-3" />

            <div class="emp-dlg__grid">
              <div>
                <div class="emp-label">Shop</div>
                <v-select v-model="campForm.shopCode" :items="knownShops" dense outlined hide-details />
              </div>
              <div>
                <div class="emp-label">Sprache</div>
                <v-select v-model="campForm.lang" :items="langItems" dense outlined hide-details />
              </div>
            </div>

            <div class="emp-label mt-3">Zielgruppe</div>
            <div class="emp-channel mb-1">
              <div
                v-for="opt in segmentItems" :key="opt.value"
                class="emp-channel__opt"
                :class="{ active: campForm.segment === opt.value }"
                @click="$set(campForm, 'segment', opt.value)"
              >{{ opt.text }}</div>
            </div>
            <template v-if="campForm.segment === 'buyers'">
              <div class="emp-dlg__grid mb-1">
                <div>
                  <div class="emp-label">Bestellungen von</div>
                  <v-text-field v-model="campForm.segmentFrom" type="date" dense outlined hide-details />
                </div>
                <div>
                  <div class="emp-label">bis (einschließlich)</div>
                  <v-text-field v-model="campForm.segmentTo" type="date" dense outlined hide-details />
                </div>
              </div>
              <div class="emp-hint mb-3">
                Alle Käufer des Zeitraums — <strong>auch ohne Marketing-Opt-in</strong> (neutrale Service-Mail,
                daher im Mail-Text keinen Rabatt erwähnen!). Abmeldungen/Bounces bleiben ausgeschlossen.
              </div>
            </template>

            <div class="emp-label mt-3">Template</div>
            <div class="emp-tplpick mb-3" @click="openTplPicker('camp')">
              <span class="emp-tplpick__name">{{ templateName(campForm.templateId) }}</span>
              <span v-if="templateIsLive(campForm.templateId)" class="emp-live-badge">LIVE</span>
              <v-spacer />
              <v-icon small>mdi-image-search-outline</v-icon>
            </div>

            <div class="emp-label">Versandkanal</div>
            <div class="emp-channel mb-1">
              <div
                v-for="c in channelItems" :key="c.value"
                class="emp-channel__opt"
                :class="{
                  active: campForm.channel === c.value,
                  'emp-channel__opt--off': c.value === 'resend' && !resendReady(campForm.shopCode),
                }"
                @click="c.value === 'resend' && !resendReady(campForm.shopCode) ? null : $set(campForm, 'channel', c.value)"
              >{{ c.text }}</div>
            </div>
            <div v-if="!resendReady(campForm.shopCode)" class="emp-hint emp-hint--warn mb-3">
              Für {{ shopLabel(campForm.shopCode) }} ist kein Resend-Absender hinterlegt (Einstellungen → Versandkanal je Shop) — Versand läuft über Workspace.
            </div>
            <div v-else class="emp-hint mb-3">Empfehlung: Resend — schützt die Reputation der Vorschau-Mails.</div>

            <div class="emp-label">Rabatt</div>
            <div class="emp-channel mb-3">
              <div
                v-for="opt in [{ text: 'Kein Rabatt', value: 'none' }, { text: 'Gemeinsamer Code für alle', value: 'shared' }]" :key="opt.value"
                class="emp-channel__opt"
                :class="{ active: campForm.discountType === opt.value }"
                @click="$set(campForm, 'discountType', opt.value)"
              >{{ opt.text }}</div>
            </div>
            <template v-if="campForm.discountType === 'shared'">
              <div class="emp-dlg__grid">
                <div>
                  <div class="emp-label">Code (muss in Shopify existieren)</div>
                  <v-text-field v-model="campForm.discountCode" dense outlined hide-details />
                </div>
                <div>
                  <div class="emp-label">Höhe (für {{ token("amount") }} im Template)</div>
                  <div class="d-flex" style="gap:8px">
                    <div class="emp-channel">
                      <div class="emp-channel__opt" :class="{ active: campForm.discountValueType === 'fixed' }" @click="$set(campForm, 'discountValueType', 'fixed')">Betrag</div>
                      <div class="emp-channel__opt" :class="{ active: campForm.discountValueType === 'percentage' }" @click="$set(campForm, 'discountValueType', 'percentage')">%</div>
                    </div>
                    <v-text-field
                      v-model.number="campForm.discountAmount"
                      type="number" dense outlined hide-details
                      style="max-width:110px"
                      :suffix="campForm.discountValueType === 'percentage' ? '%' : shopCurrency(campForm)"
                    />
                  </div>
                </div>
              </div>
            </template>

            <div class="emp-hint mt-3">
              <template v-if="campForm.segment === 'buyers'">
                Zielgruppe: <strong>alle Käufer im gewählten Zeitraum</strong> (abzüglich Abmeldungen).
              </template>
              <template v-else>
                Zielgruppe: <strong>alle Marketing-Opt-ins</strong> des Shops (abzüglich Abmeldungen).
              </template>
              Empfängerzahl über „Zählen“ an der Kampagnen-Karte; Start erst nach Pflicht-Testversand.
            </div>
          </div>
          <div class="emp-dlg__actions">
            <v-btn text @click="campDialog = false">Abbrechen</v-btn>
            <v-spacer />
            <v-btn color="#7b5cd6" dark depressed :loading="savingCamp" @click="saveCampaign">Speichern</v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- ═══ Dialog: Template wählen (visuell) ═══ -->
      <v-dialog v-model="tplPicker.show" max-width="760">
        <v-card class="emp-dlg">
          <div class="emp-dlg__title">
            <v-icon small class="emp-card__icon">mdi-image-search-outline</v-icon>
            Template wählen
          </div>
          <div class="emp-dlg__body pb-4">
            <div class="emp-gallery emp-gallery--picker">
              <div
                v-for="tpl in templates" :key="tpl.id"
                class="emp-tplcard emp-tplcard--pick"
                :class="{ 'emp-tplcard--selected': pickerValue === tpl.id }"
                @click="pickTemplate(tpl.id)"
              >
                <div class="emp-tplcard__preview emp-tplcard__preview--small">
                  <div class="emp-tplcard__scale emp-tplcard__scale--small" v-html="cardPreview(tpl)" />
                </div>
                <div class="emp-tplcard__meta">
                  <div class="emp-tplcard__name">
                    {{ tpl.name || tpl.id }}
                    <div class="emp-tplcard__badges">
                      <span v-if="templateIsLive(tpl.id)" class="emp-live-badge">LIVE</span>
                    </div>
                  </div>
                  <v-spacer />
                  <v-icon v-if="pickerValue === tpl.id" small color="#7b5cd6">mdi-check-circle</v-icon>
                </div>
              </div>
            </div>
          </div>
        </v-card>
      </v-dialog>

      <!-- ═══ Dialog: Start bestätigen ═══ -->
      <v-dialog v-model="startDialog" max-width="440">
        <v-card class="emp-dlg">
          <div class="emp-dlg__title">
            <v-icon small class="emp-card__icon">mdi-send</v-icon>
            Kampagne starten?
          </div>
          <div class="emp-dlg__body">
            <template v-if="startMode === 'pilot'">
              <p>
                „<strong>{{ startCampaign && startCampaign.name }}</strong>“ ({{ startCampaign && shopLabel(startCampaign.shopCode) }},
                <strong>{{ startAudienceLabel }}</strong> Empfänger) startet als <strong>Pilot</strong>:
                Es werden zunächst nur die ersten
                <v-text-field
                  v-model.number="startPilotCount" type="number" dense outlined hide-details
                  style="max-width: 80px; display: inline-block" class="mx-1"
                />
                Mails gesendet.
              </p>
              <p class="emp-hint">
                Danach stoppt die Kampagne automatisch („Pilot gesendet — prüfen“). Wenn Mail, Umfrage und
                Zahlen passen, gibst du den Rest über den Play-Button („Komplett starten“) frei.
              </p>
            </template>
            <template v-else-if="startMode === 'full'">
              <p>
                Pilot ist abgeschlossen ({{ startCampaign && startCampaign.counters && startCampaign.counters.sent || 0 }} gesendet, inkl. Test-Mails).
                Jetzt werden die <strong>verbleibenden {{ startCampaign ? campaignOpen(startCampaign).toLocaleString('de-DE') : 0 }}</strong>
                Empfänger angeschrieben — bereits angeschriebene bekommen nichts doppelt.
              </p>
            </template>
            <template v-else>
              <p>
                „<strong>{{ startCampaign && startCampaign.name }}</strong>“ wird fortgesetzt
                ({{ startCampaign ? campaignOpen(startCampaign).toLocaleString('de-DE') : 0 }} Empfänger verbleibend).
              </p>
            </template>
            <p v-if="settingsDoc.testMode" class="emp-hint emp-hint--warn">
              Testmodus ist aktiv: Es wird nur an Test-Adressen wirklich gesendet, alle anderen werden übersprungen.
            </p>
          </div>
          <div class="emp-dlg__actions">
            <v-btn text @click="startDialog = false">Abbrechen</v-btn>
            <v-spacer />
            <v-btn color="#7b5cd6" dark depressed @click="doStart">
              {{ startMode === 'pilot' ? 'Pilot starten' : startMode === 'full' ? 'Komplett starten' : 'Fortsetzen' }}
            </v-btn>
          </div>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import StateDisplay from "@/components/StateDisplay";
import SurveyResultsPanel from "@/components/EmailMarketing/SurveyResultsPanel";
import {
  addDoc, collection, deleteDoc, doc, onSnapshot, query, setDoc, where, Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions as fbFunctions } from "../../../fb";

const LANGS = [
  { value: "de", text: "Deutsch" },
  { value: "en", text: "Englisch" },
  { value: "fr", text: "Französisch" },
  { value: "es", text: "Spanisch" },
  { value: "it", text: "Italienisch" },
  { value: "nl", text: "Niederländisch" },
  { value: "pl", text: "Polnisch" },
  { value: "pt", text: "Portugiesisch" },
  { value: "da", text: "Dänisch" },
  { value: "sv", text: "Schwedisch" },
  { value: "cs", text: "Tschechisch" },
  { value: "sl", text: "Slowenisch" },
  { value: "sk", text: "Slowakisch" },
];

const SHOP_LABELS = { AQ_DE: "DE", AQ_FR: "FR", AQ_ES: "ES", AQ_PL: "PL", OH_US: "OhMy" };

const PLACEHOLDER_DOCS = [
  { token: "{{salutation}}", hint: "Anrede inkl. Name, z. B. \u201EHallo Anna,\u201C" },
  { token: "{{firstName}}", hint: "Vorname des Kunden (kann leer sein)" },
  { token: "{{previewImage}}", hint: "URL des Kunden-Kunstwerks (kann leer sein)" },
  { token: "{{editorLink}}", hint: "Link, der das Kunstwerk im Live Editor l\u00E4dt" },
  { token: "{{discountCode}}", hint: "Einmal-Rabattcode (nur Mail 2)" },
  { token: "{{amount}}", hint: "Rabattbetrag, z. B. 5 \u20AC" },
  { token: "{{min}}", hint: "Mindestbestellwert, z. B. 30 \u20AC" },
  { token: "{{logoUrl}}", hint: "Shop-Logo-URL" },
  { token: "{{brandName}}", hint: "Markenname, z. B. Artquarell" },
  { token: "{{optoutLink}}", hint: "Abmelde-Link (PFLICHT)" },
];

// Beispiel-Kunstwerk für die Vorschau (neutraler SVG-Platzhalter)
const SAMPLE_IMG =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="240"><rect width="320" height="240" rx="10" fill="#e8e2d5"/><text x="160" y="126" font-family="Helvetica" font-size="15" fill="#8a8377" text-anchor="middle">Kunstwerk des Kunden</text></svg>'
  );

/* Basis für "Neues Template": schlankes, funktionierendes Gerüst */
const STARTER_HTML = `<div style="margin:0 auto;max-width:560px;font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;">
  <div style="text-align:center;padding:28px 0 8px;">{{#if logoUrl}}<img src="{{logoUrl}}" alt="{{brandName}}" width="180" style="width:180px;max-width:60%;">{{/if}}</div>
  <h1 style="font-size:26px;font-weight:normal;text-align:center;margin:18px 0 6px;">{{t.title}}</h1>
  <p style="font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.6;color:#444;text-align:center;margin:0 24px 22px;">{{salutation}} {{t.intro}}</p>
  {{#if discountCode}}<div style="text-align:center;margin:0 0 20px;">
    <div style="display:inline-block;background:#f7f6f3;border:2px dashed #1a1a1a;border-radius:10px;padding:14px 30px;">
      <span style="font-family:Helvetica,Arial,sans-serif;font-size:13px;color:#666;">{{t.codeLabel}}</span><br>
      <span style="font-family:Menlo,Consolas,monospace;font-size:24px;letter-spacing:2px;">{{discountCode}}</span>
    </div>
  </div>{{/if}}
  <div style="text-align:center;margin:0 0 30px;">
    <a href="{{editorLink}}" style="display:inline-block;background:#1a1a1a;color:#ffffff;text-decoration:none;font-family:Helvetica,Arial,sans-serif;font-size:16px;padding:15px 34px;border-radius:8px;">{{t.cta}}</a>
  </div>
  <p style="font-family:Helvetica,Arial,sans-serif;font-size:12px;color:#999;line-height:1.6;text-align:center;margin:34px 24px 8px;">{{t.footer}}</p>
  <p style="font-family:Helvetica,Arial,sans-serif;font-size:11px;color:#bbb;text-align:center;margin:0 0 28px;"><a href="{{optoutLink}}" style="color:#bbb;">{{t.optout}}</a></p>
</div>`;

const STARTER_TEXTS = {
  de: {
    subject: "Betreff hier",
    hello: "Hallo",
    title: "Überschrift hier",
    intro: "dein Einleitungstext hier.",
    codeLabel: "Dein Code",
    cta: "Zum Shop",
    footer: "Du hast Fragen? Antworte einfach auf diese E-Mail.",
    optout: "Keine Mails mehr erhalten",
  },
  en: {
    subject: "Subject here",
    hello: "Hi",
    title: "Headline here",
    intro: "your intro text here.",
    codeLabel: "Your code",
    cta: "Visit shop",
    footer: "Questions? Just reply to this email.",
    optout: "Unsubscribe",
  },
};

/* ── Client-Renderer: identische Logik wie templateRenderer.js im Backend ── */
const renderTexts = (html, texts) =>
  String(html).replace(/\{\{t\.([a-zA-Z0-9_]+)\}\}/g, (m, key) => {
    const v = texts && texts[key];
    return v === undefined || v === null ? "" : String(v);
  });
const renderConditionals = (html, data) =>
  String(html).replace(/\{\{#if\s+([a-zA-Z0-9_.]+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (m, key, body) =>
    data[key] ? body : ""
  );
const renderData = (html, data) =>
  String(html).replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (m, key) => {
    const v = data[key];
    return v === undefined || v === null ? "" : String(v);
  });

export default {
  name: "EmailMarketingPanel",
  components: { StateDisplay, SurveyResultsPanel },
  data() {
    return {
      loading: true,
      settingsDoc: {},
      docs: [],
      rangeDays: 14,
      rangeItems: [
        { text: "Letzte 7 Tage", value: 7 },
        { text: "Letzte 14 Tage", value: 14 },
        { text: "Letzte 30 Tage", value: 30 },
        { text: "Letzte 60 Tage", value: 60 },
      ],
      knownShops: ["AQ_DE", "AQ_FR", "AQ_ES", "AQ_PL", "OH_US"],
      channelItems: [
        { text: "Workspace", value: "workspace" },
        { text: "Resend", value: "resend" },
      ],
      form: {
        testMode: true,
        mail1DelayHours: 12,
        mail2DelayHours: 36,
        shops: [],
        senders: {},
        allowedTestEmails: [],
        mail1TemplateId: "abandoned-mail-1",
        mail2TemplateId: "abandoned-mail-2",
      },
      section: "automations",
      sections: [
        { value: "automations", text: "Automations", icon: "mdi-robot-outline" },
        { value: "templates", text: "Templates", icon: "mdi-code-tags" },
        { value: "campaigns", text: "Kampagnen", icon: "mdi-send-outline" },
        { value: "survey", text: "Umfrage", icon: "mdi-clipboard-text-outline" },
        { value: "settings", text: "Einstellungen", icon: "mdi-tune-variant" },
      ],
      segmentItems: [
        { text: "Marketing-Opt-ins", value: "optins" },
        { text: "Käufer im Zeitraum", value: "buyers" },
      ],
      // Automationen
      automations: [],
      autoDialog: false,
      autoForm: {},
      savingAuto: false,
      // Kampagnen
      campaigns: [],
      campDialog: false,
      campForm: {},
      savingCamp: false,
      countingId: null,
      testingId: null,
      startDialog: false,
      startCampaign: null,
      startPilotCount: 20,
      tplPicker: { show: false, target: null },
      audienceCache: {},
      reportShops: ["AQ_DE"], // Shops mit read_reports → Opt-in-Zählung
      testDialog: { show: false, campaign: null, mode: "", custom: "" },
      unsubAutomations: null,
      unsubCampaigns: null,
      unsubAudience: null,
      // Template-Builder
      builderView: "grid",
      templates: [],
      selectedTemplateId: null,
      tplName: "",
      tplHtml: "",
      tplTexts: {},
      textsJson: "",
      allLangsJson: "",
      editorTab: "html",
      tplError: "",
      lang: "de",
      langItems: LANGS,
      placeholderDocs: PLACEHOLDER_DOCS,
      ifSyntax: "{{#if previewImage}}…{{/if}}",
      saving: false,
      savedAt: null,
      savingTpl: false,
      savedTplAt: null,
      unsubSettings: null,
      unsubDocs: null,
      unsubTemplates: null,
    };
  },
  computed: {
    // Start-Modus: erster Start = Pflicht-Pilot; nach Pilot = Vollversand
    // freigeben; nach Vollversand-Freigabe = normales Fortsetzen
    startMode() {
      const c = this.startCampaign;
      if (!c) return "pilot";
      if (c.fullSendAt) return "resume";
      if (c.pilotDoneAt) return "full";
      return "pilot";
    },
    senderShops() {
      // Aktive Abbrecher-Shops + Shops mit Kampagnen (für deren Resend-Absender)
      const set = new Set(this.form.shops || []);
      this.campaigns.forEach((c) => c.shopCode && set.add(c.shopCode));
      return this.knownShops.filter((sc) => set.has(sc));
    },
    resendConfiguredAnywhere() {
      const senders = (this.form && this.form.senders) || {};
      return Object.values(senders).some((s) => s && s.resendFrom);
    },
    templateItems() {
      return this.templates.map((t) => ({ text: t.name || t.id, value: t.id }));
    },
    eventItems() {
      return [
        { text: "Bestellung aufgegeben (X Std./Tage danach)", value: "order_placed" },
        { text: "Checkout-Abbruch (durch Mail 1/2 abgedeckt)", value: "checkout_abandoned", disabled: !this.autoForm.id },
        { text: "Warenkorb erstellt — keine E-Mail-Adresse verfügbar", value: "cart_created", disabled: true },
        { text: "Versandbestätigung — folgt später", value: "fulfillment_shipped", disabled: true },
      ];
    },
    startAudienceLabel() {
      if (!this.startCampaign) return "?";
      const a = this.audienceFor(this.startCampaign);
      return a.count != null ? a.count.toLocaleString("de-DE") : "wird noch geladen";
    },
    pickerValue() {
      return this.tplPicker.target === "camp" ? this.campForm.templateId : this.autoForm.templateId;
    },
    discountTypeItems() {
      const base = [
        { text: "Kein Rabatt", value: "none" },
        { text: "Gemeinsamer Code", value: "shared" },
      ];
      if (this.autoForm.event === "checkout_abandoned") {
        base.push({ text: "Einmal-Codes pro Empfänger", value: "unique" });
      }
      return base;
    },
    eventHint() {
      if (this.autoForm.event === "order_placed") {
        return "Läuft für jede neue Bestellung: z. B. Review-Bitte nach 7 Tagen (168h) oder Cross-Sell nach 30 Tagen (720h). Nur an Marketing-Opt-ins.";
      }
      if (this.autoForm.event === "checkout_abandoned" && !this.autoForm.id) {
        return "Weitere Abbruch-Stufen sind aktuell nicht anlegbar — Mail 1/2 decken den Flow ab.";
      }
      return "";
    },
    sampleData() {
      return {
        salutation: (this.currentTexts.hello || "Hallo") + " Anna,",
        firstName: "Anna",
        previewImage: SAMPLE_IMG,
        editorLink: "#",
        discountCode: "WB5DEMO42",
        amount: "5 \u20AC",
        min: "30 \u20AC",
        logoUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
        noLogo: "",
        brandName: "Artquarell",
        optoutLink: "#",
      };
    },
    currentTexts() {
      return this.tplTexts[this.lang] || this.tplTexts.en || this.tplTexts.de || {};
    },
    mergedPreviewData() {
      // Sprach-JSON + Beispiel-Laufzeitdaten; Laufzeit gewinnt (wie Backend)
      return { ...this.currentTexts, ...this.sampleData };
    },
    previewHtml() {
      let html = renderTexts(this.tplHtml, this.currentTexts);
      html = renderConditionals(html, this.mergedPreviewData);
      return renderData(html, this.mergedPreviewData);
    },
    previewSubject() {
      const subj = renderData(String(this.currentTexts.subject || ""), this.mergedPreviewData);
      return subj || "(kein subject im Platzhalter-JSON)";
    },
    statRows() {
      const byShop = {};
      for (const d of this.docs) {
        const shop = d.shopCode || "?";
        const row = (byShop[shop] = byShop[shop] || {
          shop, captured: 0, withEmail: 0, mail1: 0, mail2: 0, dry1: 0, dry2: 0, converted: 0, codeRedeemed: 0, delivered: 0, opened: 0, clicked: 0, complained: 0,
        });
        row.captured++;
        if (d.email) row.withEmail++;
        if (d.mail1SentAt) row.mail1++;
        if (d.mail2SentAt) row.mail2++;
        if (d.dryRun && d.dryRun.mail1At) row.dry1++;
        if (d.dryRun && d.dryRun.mail2At) row.dry2++;
        if (d.convertedAt) row.converted++;
        if (d.convertedVia === "mail2_code") row.codeRedeemed++;
        // Resend-Webhook-Events (nur für Resend-Kanal verfügbar)
        if (d.mail1Delivered) row.delivered++;
        if (d.mail2Delivered) row.delivered++;
        if (d.mail1Opened) row.opened++;
        if (d.mail2Opened) row.opened++;
        if (d.mail1Clicked) row.clicked++;
        if (d.mail2Clicked) row.clicked++;
        if (d.mail1Complained || d.mail1Bounced) row.complained++;
        if (d.mail2Complained || d.mail2Bounced) row.complained++;
      }
      return Object.values(byShop).sort((a, b) => b.captured - a.captured);
    },
    totals() {
      const t = { captured: 0, withEmail: 0, mail1: 0, mail2: 0, dry1: 0, dry2: 0, converted: 0, codeRedeemed: 0, delivered: 0, opened: 0, clicked: 0, complained: 0 };
      for (const r of this.statRows) for (const k of Object.keys(t)) t[k] += r[k];
      return t;
    },
  },
  watch: {
    "campForm.shopCode"(shopCode) {
      if (this.campForm.channel === "resend" && !this.resendReady(shopCode)) {
        this.$set(this.campForm, "channel", "workspace");
      }
    },
    rangeDays() {
      this.subscribeDocs();
    },
    selectedTemplateId() {
      this.loadSelectedTemplate();
    },
    lang() {
      // Beim Sprachwechsel im Platzhalter-Tab: aktuelles JSON übernehmen, dann neue Sprache zeigen
      if (this.editorTab === "texts") {
        if (!this.applyTextsJson()) return;
        this.textsJson = JSON.stringify(this.tplTexts[this.lang] || {}, null, 2);
      }
    },
  },
  created() {
    this.unsubSettings = onSnapshot(doc(db, "settings", "abandonedMail"), (snap) => {
      this.settingsDoc = snap.data() || {};
      if (!this.formInitialized) {
        const s = this.settingsDoc;
        this.form = {
          testMode: s.testMode !== false,
          mail1DelayHours: s.mail1DelayHours ?? 12,
          mail2DelayHours: s.mail2DelayHours ?? 36,
          shops: Array.isArray(s.shops) ? [...s.shops] : ["AQ_DE"],
          senders: JSON.parse(JSON.stringify(s.senders || {})),
          allowedTestEmails: Array.isArray(s.allowedTestEmails) ? [...s.allowedTestEmails] : [],
          mail1TemplateId: s.mail1TemplateId || "abandoned-mail-1",
          mail2TemplateId: s.mail2TemplateId || "abandoned-mail-2",
        };
        this.formInitialized = true;
      }
      this.loading = false;
    });
    this.unsubAutomations = onSnapshot(collection(db, "builderMailAutomations"), (snap) => {
      this.automations = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.event + (a.stage || 0)).localeCompare(b.event + (b.stage || 0)));
    });
    this.unsubCampaigns = onSnapshot(collection(db, "builderMailCampaigns"), (snap) => {
      this.campaigns = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
      this.ensureAudienceCounts();
    });
    this.unsubAudience = onSnapshot(collection(db, "builderAudienceCache"), (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        map[d.id] = { ...data, at: data.at && data.at.toMillis ? data.at.toMillis() : data.at };
      });
      this.audienceCache = map;
    });
    this.unsubTemplates = onSnapshot(collection(db, "builderMailTemplates"), (snap) => {
      this.templates = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (!this.selectedTemplateId && this.templates.length) {
        this.selectedTemplateId = this.templates[0].id;
      }
    });
    this.subscribeDocs();
  },
  beforeDestroy() {
    if (this.unsubSettings) this.unsubSettings();
    if (this.unsubDocs) this.unsubDocs();
    if (this.unsubTemplates) this.unsubTemplates();
    if (this.unsubAutomations) this.unsubAutomations();
    if (this.unsubCampaigns) this.unsubCampaigns();
    if (this.unsubAudience) this.unsubAudience();
  },
  methods: {
    token(key) {
      return "{{" + key + "}}";
    },
    pct(part, total) {
      if (!total) return "";
      return Math.round((part / total) * 100) + " %";
    },
    shopLabel(code) {
      return SHOP_LABELS[code] || code;
    },
    subscribeDocs() {
      if (this.unsubDocs) this.unsubDocs();
      const from = Timestamp.fromMillis(Date.now() - this.rangeDays * 24 * 3600e3);
      const q = query(collection(db, "abandonedCheckouts"), where("createdAt", ">=", from));
      this.unsubDocs = onSnapshot(q, (snap) => {
        this.docs = snap.docs.map((d) => d.data());
      });
    },
    senderFor(shop) {
      if (!this.form.senders[shop]) {
        this.$set(this.form.senders, shop, { mail1: "workspace", mail2: "workspace", resendFrom: "", resendFromName: "" });
      }
      return this.form.senders[shop];
    },
    async saveSettings() {
      this.saving = true;
      try {
        await setDoc(
          doc(db, "settings", "abandonedMail"),
          {
            testMode: this.form.testMode,
            mail1DelayHours: this.form.mail1DelayHours,
            mail2DelayHours: this.form.mail2DelayHours,
            shops: this.form.shops,
            senders: this.form.senders,
            allowedTestEmails: this.form.allowedTestEmails,
            mail1TemplateId: this.form.mail1TemplateId,
            mail2TemplateId: this.form.mail2TemplateId,
          },
          { merge: true }
        );
        this.savedAt = Date.now();
        setTimeout(() => (this.savedAt = null), 2500);
      } finally {
        this.saving = false;
      }
    },
    /* ── Modus-Umschalter ── */
    async toggleMode() {
      const goingLive = !!this.settingsDoc.testMode;
      const msg = goingLive
        ? "Auf LIVE schalten? Ab sofort erhalten ECHTE Kunden Automations- und Kampagnen-Mails."
        : "In den Testmodus wechseln? Es wird nur noch an Test-Adressen gesendet.";
      if (!window.confirm(msg)) return;
      await setDoc(doc(db, "settings", "abandonedMail"), { testMode: !goingLive }, { merge: true });
      this.form.testMode = !goingLive;
    },
    /* ── Template-Picker & Verwaltung ── */
    templateIsLive(id) {
      return this.automations.some((a) => a.templateId === id && a.active !== false);
    },
    templateUsage(id) {
      const usage = [];
      for (const a of this.automations) {
        if (a.templateId === id) usage.push(a.name || this.eventLabel(a.event));
      }
      for (const c of this.campaigns) {
        if (c.templateId === id && ["draft", "building", "sending", "paused"].includes(c.status || "draft")) {
          usage.push("Kampagne: " + (c.name || c.id));
        }
      }
      return usage.slice(0, 2);
    },
    openTplPicker(target) {
      this.tplPicker = { show: true, target };
    },
    pickTemplate(id) {
      if (this.tplPicker.target === "camp") this.$set(this.campForm, "templateId", id);
      else this.$set(this.autoForm, "templateId", id);
      this.tplPicker.show = false;
    },
    async createBlankTemplate() {
      const ref = await addDoc(collection(db, "builderMailTemplates"), {
        name: "Neues Template",
        html: STARTER_HTML,
        texts: STARTER_TEXTS,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      this.openEditor(ref.id);
    },
    async deleteTemplate(tpl) {
      const usage = this.templateUsage(tpl.id);
      if (usage.length) {
        alert("Template wird noch verwendet von: " + usage.join(", ") + ". Erst dort austauschen.");
        return;
      }
      if (!window.confirm(`Template „${tpl.name || tpl.id}“ endgültig löschen?`)) return;
      await deleteDoc(doc(db, "builderMailTemplates", tpl.id));
    },
    async deleteAutomation(a) {
      if (!window.confirm(`Automation „${a.name}“ endgültig löschen?`)) return;
      await deleteDoc(doc(db, "builderMailAutomations", a.id));
    },
    async deleteCampaign(c) {
      if (!window.confirm(`Kampagne „${c.name}“ endgültig löschen?`)) return;
      await deleteDoc(doc(db, "builderMailCampaigns", c.id));
    },
    /* ── Automationen ── */
    shopCurrency(form) {
      // Festbeträge laufen in der Shop-Währung; hier nur die Anzeige
      const code = form.shopCode || "AQ_DE";
      return code === "OH_US" ? "$" : code === "AQ_PL" ? "zł" : "€";
    },
    eventLabel(event) {
      return (
        { checkout_abandoned: "Checkout-Abbruch", order_placed: "Bestellung", cart_created: "Warenkorb", fulfillment_shipped: "Versand" }[event] || event
      );
    },
    templateName(id) {
      const t = this.templates.find((x) => x.id === id);
      return t ? t.name || id : id || "—";
    },
    openNewAutomation() {
      this.autoForm = {
        id: null,
        event: "order_placed",
        name: "",
        delayHours: 168,
        templateId: this.templates[0] && this.templates[0].id,
        channel: "resend",
        requiresOptIn: true,
        discountType: "none",
        discountCode: "",
        discountValueType: "fixed",
        discountAmount: 5,
        discountMinSubtotal: 30,
      };
      this.autoDialog = true;
    },
    openEditAutomation(a) {
      this.autoForm = {
        id: a.id,
        event: a.event,
        name: a.name || "",
        delayHours: a.delayHours,
        templateId: a.templateId,
        channel: a.channel || "workspace",
        requiresOptIn: a.requiresOptIn !== false,
        discountType: (a.discount && a.discount.type) || "none",
        discountCode: (a.discount && a.discount.code) || "",
        discountValueType: (a.discount && a.discount.valueType) || "fixed",
        discountAmount: (a.discount && a.discount.amount) || 5,
        discountMinSubtotal: (a.discount && a.discount.minSubtotal) || 30,
      };
      this.autoDialog = true;
    },
    async saveAutomation() {
      this.savingAuto = true;
      try {
        const f = this.autoForm;
        const discount =
          f.discountType === "none"
            ? { type: "none" }
            : {
                type: f.discountType,
                valueType: f.discountValueType || "fixed",
                amount: Number(f.discountAmount) || 5,
                minSubtotal: Number(f.discountMinSubtotal) || 0,
                ...(f.discountType === "shared" ? { code: (f.discountCode || "").trim() } : {}),
              };
        const payload = {
          event: f.event,
          name: f.name || this.eventLabel(f.event),
          delayHours: Number(f.delayHours) || 24,
          templateId: f.templateId,
          channel: f.channel || "workspace",
          requiresOptIn: !!f.requiresOptIn,
          discount,
          updatedAt: new Date().toISOString(),
        };
        if (f.id) {
          await setDoc(doc(db, "builderMailAutomations", f.id), payload, { merge: true });
        } else {
          await addDoc(collection(db, "builderMailAutomations"), { ...payload, active: false, createdAt: new Date().toISOString() });
        }
        this.autoDialog = false;
      } finally {
        this.savingAuto = false;
      }
    },
    toggleAutomation(a, val) {
      setDoc(doc(db, "builderMailAutomations", a.id), { active: !!val }, { merge: true });
    },
    /* ── Kampagnen ── */
    statusLabel(st) {
      return (
        { draft: "Entwurf", building: "Baut Liste…", sending: "Versand läuft", paused: "Pausiert", done: "Fertig", error: "Fehler" }[st || "draft"] || st
      );
    },
    statusLabelFor(c) {
      if (c.status === "paused" && c.pilotDoneAt && !c.fullSendAt) return "Pilot gesendet — prüfen";
      if (c.status === "sending" && !c.fullSendAt) return "Pilot läuft…";
      return this.statusLabel(c.status);
    },
    openNewCampaign() {
      this.campForm = {
        id: null,
        name: "",
        shopCode: "AQ_DE",
        templateId: this.templates[0] && this.templates[0].id,
        lang: "de",
        channel: this.resendReady("AQ_DE") ? "resend" : "workspace",
        segment: "optins",
        segmentFrom: "",
        segmentTo: "",
        discountType: "none",
        discountCode: "",
        discountValueType: "fixed",
        discountAmount: 5,
      };
      this.campDialog = true;
    },
    openEditCampaign(c) {
      this.campForm = {
        id: c.id,
        name: c.name || "",
        shopCode: c.shopCode,
        templateId: c.templateId,
        lang: c.lang || "de",
        channel: (c.channel || "resend") === "resend" && !this.resendReady(c.shopCode) ? "workspace" : c.channel || "resend",
        segment: c.segment || "optins",
        segmentFrom: c.segmentFrom || "",
        segmentTo: c.segmentTo || "",
        discountType: (c.discount && c.discount.type) || "none",
        discountCode: (c.discount && c.discount.code) || "",
        discountValueType: (c.discount && c.discount.valueType) || "fixed",
        discountAmount: (c.discount && c.discount.amount) || 5,
      };
      this.campDialog = true;
    },
    async saveCampaign() {
      this.savingCamp = true;
      try {
        const f = this.campForm;
        const payload = {
          name: f.name || "Kampagne",
          shopCode: f.shopCode,
          templateId: f.templateId,
          lang: f.lang,
          channel: f.channel,
          segment: f.segment || "optins",
          segmentFrom: f.segment === "buyers" ? f.segmentFrom || "" : "",
          segmentTo: f.segment === "buyers" ? f.segmentTo || "" : "",
          discount:
            f.discountType === "shared"
              ? {
                  type: "shared",
                  code: (f.discountCode || "").trim(),
                  valueType: f.discountValueType || "fixed",
                  amount: Number(f.discountAmount) || 0,
                }
              : { type: "none" },
          updatedAt: new Date().toISOString(),
        };
        if (f.id) {
          await setDoc(doc(db, "builderMailCampaigns", f.id), payload, { merge: true });
        } else {
          await addDoc(collection(db, "builderMailCampaigns"), { ...payload, status: "draft", createdAt: new Date().toISOString() });
        }
        this.campDialog = false;
      } finally {
        this.savingCamp = false;
      }
    },
    async campaignAction(params) {
      const call = httpsCallable(fbFunctions, "builderCampaignAction", { timeout: 300000 });
      const { data } = await call(params);
      return data;
    },
    fmtDate(d) {
      if (!d) return "—";
      const s = String(d).slice(0, 10).split("-");
      return s.length === 3 ? `${s[2]}.${s[1]}.${s[0].slice(2)}` : String(d);
    },
    // Käufer-Segment: Cache-Key trägt den Zeitraum (Backend schreibt denselben)
    audienceKey(c) {
      return c.segment === "buyers" ? `${c.shopCode}|buyers|${c.segmentFrom}|${c.segmentTo}` : c.shopCode;
    },
    audienceFor(c) {
      return this.audienceCache[this.audienceKey(c)] || {};
    },
    canCount(c) {
      // Käufer-Zählung läuft über Firestore und geht für jeden Shop;
      // Opt-in-Zählung braucht den Shopify-Reports-Token (nur DE)
      if (c.segment === "buyers") return !!(c.segmentFrom && c.segmentTo);
      return this.reportShops.includes(c.shopCode);
    },
    resendReady(shopCode) {
      const senders = (this.form && this.form.senders) || {};
      return !!(senders[shopCode] && senders[shopCode].resendFrom);
    },
    cnt(c, key) {
      return (c.counters && c.counters[key]) || 0;
    },
    campaignOpen(c) {
      const cn = c.counters || {};
      return Math.max(0, (cn.queued || 0) - (cn.sent || 0) - (cn.skipped || 0));
    },
    audienceAge(c) {
      const at = this.audienceFor(c).at;
      if (!at || typeof at !== "number") return "";
      const min = Math.floor((Date.now() - at) / 60000);
      if (min < 2) return "gerade aktualisiert";
      if (min < 60) return `vor ${min} Min`;
      const h = Math.floor(min / 60);
      if (h < 24) return `vor ${h} h`;
      return `vor ${Math.floor(h / 24)} T`;
    },
    openTestDialog(c) {
      this.testDialog = {
        show: true,
        campaign: c,
        mode: (this.form.allowedTestEmails && this.form.allowedTestEmails[0]) || "__custom__",
        custom: "",
      };
    },
    async doSendTest() {
      const c = this.testDialog.campaign;
      const to = this.testDialog.mode === "__custom__" ? (this.testDialog.custom || "").trim() : this.testDialog.mode;
      if (!to) return;
      this.testingId = c.id;
      try {
        await this.campaignAction({ action: "test", campaignId: c.id, toEmail: to });
        this.testDialog.show = false;
      } catch (e) {
        alert("Testversand fehlgeschlagen: " + (e.message || e));
      } finally {
        this.testingId = null;
      }
    },
    async countAudience(c, force) {
      this.countingId = c.id;
      try {
        const params = { action: "audienceCount", shopCode: c.shopCode, force: !!force };
        if (c.segment === "buyers") {
          params.segment = "buyers";
          params.from = c.segmentFrom;
          params.to = c.segmentTo;
        }
        await this.campaignAction(params);
      } catch (e) {
        alert("Zählung fehlgeschlagen: " + (e.message || e));
      } finally {
        this.countingId = null;
      }
    },
    // Beim Laden für jede Zielgruppe einmal automatisch zählen
    ensureAudienceCounts() {
      this._auditRequested = this._auditRequested || new Set();
      for (const c of this.campaigns) {
        if (!this.canCount(c)) continue;
        const key = this.audienceKey(c);
        if (this.audienceCache[key] || this._auditRequested.has(key)) continue;
        this._auditRequested.add(key);
        const params = { action: "audienceCount", shopCode: c.shopCode };
        if (c.segment === "buyers") {
          params.segment = "buyers";
          params.from = c.segmentFrom;
          params.to = c.segmentTo;
        }
        this.campaignAction(params).catch(() => {});
      }
    },
    confirmStart(c) {
      this.startCampaign = c;
      this.startPilotCount = Number(c.pilotCount) || 20;
      this.startDialog = true;
    },
    async doStart() {
      const c = this.startCampaign;
      const params = { action: "start", campaignId: c.id };
      if (this.startMode === "pilot") params.pilotCount = Number(this.startPilotCount) || 20;
      if (this.startMode === "full") params.full = true;
      this.startDialog = false;
      try {
        await this.campaignAction(params);
      } catch (e) {
        alert("Start fehlgeschlagen: " + (e.message || e));
      }
    },
    /* ── Template-Builder ── */
    cardPreview(tpl) {
      const texts = tpl.texts?.[this.lang] || tpl.texts?.de || tpl.texts?.en || {};
      const hello = texts.hello || "Hallo";
      const data = { ...texts, ...this.sampleData, salutation: hello + " Anna," };
      let html = renderTexts(tpl.html || "", texts);
      html = renderConditionals(html, data);
      return renderData(html, data);
    },
    openEditor(id) {
      this.selectedTemplateId = id;
      this.loadSelectedTemplate();
      this.builderView = "edit";
      this.editorTab = "html";
    },
    // (duplicate nutzt tpl direkt, kein Editor-Zustand)
    async duplicateTemplate(tpl) {
      await addDoc(collection(db, "builderMailTemplates"), {
        name: (tpl.name || "Template") + " (Kopie)",
        html: tpl.html || "",
        texts: JSON.parse(JSON.stringify(tpl.texts || {})),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    },
    loadSelectedTemplate() {
      const tpl = this.templates.find((t) => t.id === this.selectedTemplateId);
      if (!tpl) return;
      this.tplName = tpl.name || "";
      this.tplHtml = tpl.html || "";
      this.tplTexts = JSON.parse(JSON.stringify(tpl.texts || {}));
      this.textsJson = JSON.stringify(this.tplTexts[this.lang] || {}, null, 2);
      this.tplError = "";
    },
    switchToTexts() {
      // vom Alle-Sprachen-Tab kommend zuerst dessen JSON übernehmen
      if (this.editorTab === "allLangs" && !this.applyAllLangsJson()) return;
      this.textsJson = JSON.stringify(this.tplTexts[this.lang] || {}, null, 2);
      this.editorTab = "texts";
    },
    switchToAllLangs() {
      if (this.editorTab === "texts" && !this.applyTextsJson()) return;
      this.allLangsJson = JSON.stringify(this.tplTexts, null, 2);
      this.editorTab = "allLangs";
    },
    applyTextsJson() {
      if (this.editorTab !== "texts") return true;
      try {
        this.$set(this.tplTexts, this.lang, JSON.parse(this.textsJson));
        this.tplError = "";
        return true;
      } catch (e) {
        this.tplError = "Platzhalter-JSON ungültig: " + e.message;
        return false;
      }
    },
    applyAllLangsJson() {
      if (this.editorTab !== "allLangs") return true;
      try {
        const parsed = JSON.parse(this.allLangsJson);
        if (typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("Erwartet: Objekt mit Ländercode-Schlüsseln (de, en, …)");
        }
        this.tplTexts = parsed;
        this.tplError = "";
        return true;
      } catch (e) {
        this.tplError = "Alle-Sprachen-JSON ungültig: " + e.message;
        return false;
      }
    },
    applyActiveJson() {
      // je nach offenem Tab die richtige JSON-Quelle übernehmen
      if (this.editorTab === "allLangs") return this.applyAllLangsJson();
      return this.applyTextsJson();
    },
    validateTemplate() {
      if (!this.tplHtml.includes("{{optoutLink}}")) {
        return "{{optoutLink}} fehlt im HTML (Pflicht für Abmeldung)";
      }
      const opens = (this.tplHtml.match(/\{\{#if/g) || []).length;
      const closes = (this.tplHtml.match(/\{\{\/if\}\}/g) || []).length;
      if (opens !== closes) {
        return "if-Blöcke unausgeglichen (" + opens + "× {{#if}}, " + closes + "× {{/if}})";
      }
      return "";
    },
    async saveTemplate() {
      if (!this.applyActiveJson()) return;
      const err = this.validateTemplate();
      if (err) {
        this.tplError = err;
        return;
      }
      this.savingTpl = true;
      try {
        await setDoc(
          doc(db, "builderMailTemplates", this.selectedTemplateId),
          {
            name: this.tplName,
            html: this.tplHtml,
            texts: this.tplTexts,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
        this.savedTplAt = Date.now();
        setTimeout(() => (this.savedTplAt = null), 2500);
      } finally {
        this.savingTpl = false;
      }
    },
    copyEditor() {
      const content = this.editorTab === "html" ? this.tplHtml : this.textsJson;
      navigator.clipboard.writeText(content).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
$accent: #7b5cd6;

.emp {
  --card-bg: #fff;
  color: #1d1d28;
}

/* ── Toolbar ── */
.emp-toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;

  &__title {
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }
  &__sub {
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.5);
  }
}
.emp-mode {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 700;
  border-radius: 999px;
  padding: 5px 14px;

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }
  &--test {
    background: #fff3e0;
    color: #b26a00;
  }
  &--live {
    background: #e8f5e9;
    color: #2e7d32;
    .emp-mode__dot {
      animation: emp-pulse 1.6s ease-in-out infinite;
    }
  }
}
@keyframes emp-pulse {
  50% { opacity: 0.35; }
}
.emp-range {
  max-width: 165px;
  ::v-deep .v-input__slot {
    background: #fff !important;
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(20, 20, 40, 0.08) !important;
  }
}

/* ── KPI-Kacheln ── */
.emp-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.emp-kpi {
  background: var(--card-bg);
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow: 0 1px 4px rgba(20, 20, 40, 0.07);

  &__value {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.5px;
    display: flex;
    align-items: baseline;
    gap: 7px;
  }
  &__pct {
    font-size: 11.5px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.45);
  }
  &__dry {
    font-size: 11px;
    font-weight: 600;
    color: #b26a00;
  }
  &__label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    margin-top: 1px;
  }
  &--accent {
    background: linear-gradient(135deg, #7b5cd6 0%, #9678e6 100%);
    color: #fff;
    .emp-kpi__label { color: rgba(255, 255, 255, 0.75); }
    .emp-kpi__pct { color: rgba(255, 255, 255, 0.85); }
  }
}

/* ── Karten ── */
.emp-card {
  background: var(--card-bg);
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 1px 4px rgba(20, 20, 40, 0.07);
  margin-bottom: 16px;

  &__head {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 14px;
    gap: 4px;
  }
  &__icon {
    color: $accent !important;
    margin-right: 6px;
  }
}

/* ── Tabelle ── */
.emp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: right;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 600;
    padding: 6px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
    &:first-child { text-align: left; }
  }
  td {
    text-align: right;
    padding: 8px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
    font-variant-numeric: tabular-nums;
    &:first-child { text-align: left; }
  }
  &__shop { font-weight: 700; }
  &__strong { font-weight: 700; color: $accent; }
  &__pct { font-size: 11px; color: rgba(0, 0, 0, 0.4); }
  &__dry { font-size: 11px; color: #b26a00; }
}
.emp-unsupported {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;

  &__chip {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
    border: 1px dashed rgba(0, 0, 0, 0.2);
    border-radius: 999px;
    padding: 2px 10px;
  }
  &__text {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.45);
    margin-left: 4px;
  }
}

/* ── Einstellungen ── */
.emp-settings {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;

  &__block--wide { grid-column: 1 / -1; }
}
.emp-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 7px;
}
.emp-delays {
  display: flex;
  gap: 10px;
}
.emp-mode-switch {
  display: inline-flex;
  background: #f1f0f5;
  border-radius: 10px;
  padding: 3px;
  cursor: pointer;
  user-select: none;

  &__opt {
    display: flex;
    align-items: center;
    font-size: 12.5px;
    font-weight: 600;
    padding: 6px 16px;
    border-radius: 8px;
    color: rgba(0, 0, 0, 0.45);
    transition: all 0.15s;

    &.active {
      background: #fff;
      color: #b26a00;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }
  }
  &--live &__opt.active {
    color: #2e7d32;
  }
}
.emp-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  line-height: 1.5;

  &--warn { color: #c62828; font-weight: 600; }
  code {
    background: #f1f0f5;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 11px;
  }
}
.emp-saved {
  color: #2e7d32;
  font-size: 12.5px;
  font-weight: 600;
  margin-right: 10px;
}

/* ── Sender ── */
.emp-senders {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
  gap: 12px;
}
.emp-sender {
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 12px;
  padding: 12px 14px;

  &__shop {
    font-weight: 700;
    font-size: 13px;
    margin-bottom: 8px;
  }
  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
  }
  &__mail {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    width: 42px;
  }
}
.emp-channel {
  display: inline-flex;
  background: #f1f0f5;
  border-radius: 8px;
  padding: 2px;

  &__opt {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 6px;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;
    transition: all 0.15s;

    &.active {
      background: #fff;
      color: $accent;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }
  }
}

/* ── Templates ── */
.emp-lang {
  max-width: 165px;
  ::v-deep .v-input__slot {
    background: #f1f0f5 !important;
    border-radius: 10px;
  }
}
.emp-templates {
  display: grid;
  grid-template-columns: minmax(320px, 5fr) 7fr;
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}
.emp-preview {
  &__bar {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }
  &__subject {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
    background: #f7f6fa;
    border-radius: 8px;
    padding: 7px 12px;
    margin-bottom: 10px;
  }
  &__frame {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    padding: 10px;
    background: #fafafa;
    max-height: 560px;
    overflow: auto;
  }
  &__raw {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    padding: 12px;
    background: #1e1e2a;
    color: #d8d8e8;
    font-size: 10.5px;
    line-height: 1.5;
    max-height: 560px;
    overflow: auto;
    white-space: pre-wrap;
  }
}
.emp-toggle {
  display: inline-flex;
  background: #f1f0f5;
  border-radius: 8px;
  padding: 2px;

  &__opt {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 14px;
    border-radius: 6px;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;

    &.active {
      background: #fff;
      color: $accent;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }
  }
}
.emp-ph,
.emp-preview__frame ::v-deep .emp-ph {
  display: inline-block;
  background: rgba(123, 92, 214, 0.12);
  color: #6a4cc6;
  border-radius: 5px;
  padding: 0 5px;
  font-family: Menlo, Consolas, monospace;
  font-size: 0.85em;
  font-style: normal;
}
.emp-fields {
  .emp-field {
    margin-bottom: 10px;

    &__key {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 3px;
    }
    &__label {
      font-size: 11.5px;
      color: rgba(0, 0, 0, 0.45);
    }
  }
}

/* ── JSON-Dialog ── */
.emp-json {
  &__area ::v-deep textarea {
    font-family: Menlo, Consolas, monospace !important;
    font-size: 12px !important;
    line-height: 1.5 !important;
  }
}

/* ── LIVE-Badge + Template-Picker ── */
.emp-live-badge {
  font-size: 10px;
  font-weight: 700;
  color: #2e7d32;
  background: #e8f5e9;
  border-radius: 999px;
  padding: 1px 8px;
  letter-spacing: 0.3px;
}
.emp-tplpick {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-radius: 6px;
  padding: 9px 12px;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: #7b5cd6;
  }
  &__name {
    font-size: 13.5px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.emp-gallery--picker {
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
}
.emp-tplcard--pick {
  cursor: pointer;
}
.emp-tplcard--selected {
  border-color: #7b5cd6;
  box-shadow: 0 0 0 2px rgba(123, 92, 214, 0.25);
}
.emp-tplcard__preview--small {
  height: 190px;
}
.emp-tplcard__scale--small {
  transform: scale(0.36);
}
.emp-mode--clickable {
  cursor: pointer;
  user-select: none;
}

/* ── Moderne Dialoge ── */
.emp-dlg {
  border-radius: 16px !important;

  &__title {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 700;
    padding: 20px 24px 4px;
  }
  &__body {
    padding: 14px 24px 4px;
  }
  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  &__switchrow {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }
  &__switchlabel {
    font-size: 13.5px;
    font-weight: 600;
    padding-top: 4px;
  }
  &__actions {
    display: flex;
    align-items: center;
    padding: 14px 24px 18px;
  }
}

/* ── Section-Switcher ── */

.emp-sections {
  display: inline-flex;
  background: #fff;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(20, 20, 40, 0.07);

  &__opt {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    padding: 7px 18px;
    border-radius: 9px;
    color: rgba(0, 0, 0, 0.5);
    cursor: pointer;
    transition: all 0.15s;

    &.active {
      background: rgba(123, 92, 214, 0.1);
      color: #6a4cc6;
    }
  }
}

/* ── Automation-Karten ── */
.emp-autos {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}
.emp-auto {
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 12px;
  /* rechts Platz für Schalter + Stift (absolut positioniert) */
  padding: 14px 112px 14px 16px;
  position: relative;
  transition: opacity 0.2s;

  &--off {
    opacity: 0.5;
  }
  &__flow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
  }
  &__event {
    background: rgba(123, 92, 214, 0.1);
    color: #6a4cc6;
    border-radius: 6px;
    padding: 2px 8px;
  }
  &__delay {
    background: #f1f0f5;
    border-radius: 6px;
    padding: 2px 8px;
  }
  &__tpl {
    background: #f1f0f5;
    border-radius: 6px;
    padding: 2px 8px;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__name {
    font-size: 13.5px;
    font-weight: 700;
  }
  &__chips {
    display: flex;
    gap: 4px;
    margin-top: 5px;
    min-height: 18px;
  }
  &__actions {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    gap: 2px;
  }
}

/* ── Kampagnen-Karten ── */
.emp-camps {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.emp-camp {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 16px 18px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(20, 20, 40, 0.05);

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  &__name {
    font-size: 15px;
    font-weight: 700;
  }
  &__meta {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    margin-left: 14px;
  }
  &__status {
    font-weight: 700;
    border-radius: 999px;
    padding: 1px 10px;
    font-size: 11px;

    &--draft { background: #f1f0f5; color: rgba(0, 0, 0, 0.55); }
    &--building { background: #fff3e0; color: #b26a00; }
    &--sending { background: #e3f2fd; color: #1565c0; }
    &--paused { background: #fff3e0; color: #b26a00; }
    &--done { background: #e8f5e9; color: #2e7d32; }
    &--error { background: #ffebee; color: #c62828; }
  }
  &__kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 10px;
    margin-top: 14px;
  }
  &__progresslabel {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
    margin-top: 5px;
    text-align: right;
  }
}
.emp-kpi--sm.emp-kpi--accent {
  background: linear-gradient(135deg, #7b5cd6, #9678e8) !important;
  border-color: transparent !important;

  .emp-kpi__value,
  .emp-kpi__label {
    color: #fff;
  }
}
.emp-kpi--sm {
  padding: 10px 14px;
  border-radius: 12px;
  background: #faf9fc;
  box-shadow: none;
  border: 1px solid rgba(0, 0, 0, 0.05);

  .emp-kpi__value {
    font-size: 20px;
  }
  .emp-kpi__label {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px;
  }
  .emp-kpi__na {
    color: rgba(0, 0, 0, 0.3);
  }
  .emp-kpi__sub {
    font-size: 10.5px;
    color: rgba(0, 0, 0, 0.4);
    margin-left: 4px;
  }
}
.emp-table__bad {
  color: #c62828;
  font-weight: 700;
}
.emp-kpi--bad {
  border-color: rgba(198, 40, 40, 0.35) !important;

  .emp-kpi__value {
    color: #c62828;
  }
}
.emp-kpi--muted {
  opacity: 0.6;
}
.emp-kpi .emp-kpi__sub {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.35);
  font-weight: 400;
}
.emp-sender__from {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 10px;
}
.emp-sender__preview {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 6px;
  padding-left: 2px;

  .emp-sender__preview-addr {
    color: rgba(0, 0, 0, 0.4);
    margin-left: 4px;
  }
}
.emp-channel__opt--off {
  opacity: 0.4;
  cursor: not-allowed;
  text-decoration: line-through;
}
.emp-channel--wrap {
  flex-wrap: wrap;
  background: transparent;
  padding: 0;
  gap: 6px;

  .emp-channel__opt {
    background: #f1f0f5;
  }
}

/* ── Template-Galerie ── *//* ── Template-Galerie ── */
.emp-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}
.emp-tplcard {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 4px 16px rgba(20, 20, 40, 0.12);
    .emp-tplcard__hover { opacity: 1; }
  }

  &__preview {
    position: relative;
    height: 260px;
    overflow: hidden;
    background: #fafafa;
    cursor: pointer;
  }
  &__scale {
    width: 560px;
    transform: scale(0.43);
    transform-origin: top left;
    pointer-events: none;
  }
  &__hover {
    position: absolute;
    inset: 0;
    background: rgba(30, 30, 42, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s;
  }
  &__meta {
    display: flex;
    align-items: flex-start;
    padding: 10px 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);

    .v-btn {
      flex-shrink: 0;
      margin-top: -2px;
    }
  }
  &__name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
  }
  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
  }
  &__badge {
    font-size: 10px;
    font-weight: 600;
    color: #6a4cc6;
    background: rgba(123, 92, 214, 0.1);
    border-radius: 999px;
    padding: 1px 8px;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* ── Template-Builder ── */
.emp-tpl-select {
  max-width: 280px;
}
.emp-tpl-name {
  max-width: 420px;
}
.emp-builder {
  display: grid;
  grid-template-columns: minmax(380px, 1fr) minmax(340px, 1fr);
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}
.emp-editor {
  &__bar {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }
}
.emp-code {
  width: 100%;
  height: 460px;
  resize: vertical;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  background: #1e1e2a;
  color: #e6e6f2;
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.55;
  padding: 14px;
  outline: none;
  white-space: pre;

  &:focus {
    border-color: #7b5cd6;
  }
}
.emp-placeholders {
  margin-top: 10px;

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;

    code {
      background: rgba(123, 92, 214, 0.1);
      color: #6a4cc6;
      border-radius: 5px;
      padding: 2px 7px;
      font-size: 11px;
      cursor: help;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
